import { useState } from 'react';
import { getCourtPageUrl } from 'constants/pages';
import { SportsEnum, VenueAccessTypesEnum, VenueNetsEnum } from 'types/generated/client';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import Location from 'svg/Location';
import PlusPlain from 'svg/PlusPlain';
import Button from 'components/Button';
import Link from 'components/Link';
import SportBadge from 'components/SportBadge';
import CardVenue from 'components/cards/CardVenue';
import classNames from 'styles/utils/classNames';
import SlotChip from './SlotChip';
import { BookingSlot } from './types';

/** How many chips a compact card shows before the "+" reveals the rest. */
const COLLAPSED_SLOT_COUNT = 3;

interface Props {
  venueId: string;
  slug: string;
  title: string;
  imageUrl?: string;
  addressString: string;
  accessType?: VenueAccessTypesEnum | null;
  totalCourtCount?: number;
  pickleballNets?: VenueNetsEnum | null;
  primarySport?: SportsEnum | null;
  slots: BookingSlot[];
  /** Cents. Rendered as "from $60" on the featured card only. */
  fromPriceUnitAmount?: number | null;
  currency?: string;
  /** Featured cards lead the feed: larger image, price, and the full slot grid. */
  isFeatured?: boolean;
  onSlotSelect: (venueId: string, slotId: string) => void;
  className?: string;
}

/**
 * A club on the home feed with the times it can take.
 *
 * The compact card is `CardVenue` — the same card every other venue surface uses
 * — with a row of bookable times under it. Only the featured card, which the
 * design gives its own layout, is built here.
 */
export default function BookableVenueCard({
  venueId,
  slug,
  title,
  imageUrl,
  addressString,
  accessType,
  totalCourtCount,
  pickleballNets,
  primarySport,
  slots,
  fromPriceUnitAmount,
  currency = 'usd',
  isFeatured = false,
  onSlotSelect,
  className,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  const now = Date.now();
  const bookable = slots.filter(
    (slot) => slot.isAvailable && !slot.isLateHours && new Date(slot.startAt).getTime() > now,
  );
  const collapsedCount = isFeatured ? 9 : COLLAPSED_SLOT_COUNT;
  const visible = isExpanded ? bookable : bookable.slice(0, collapsedCount);
  const hiddenCount = bookable.length - visible.length;

  const fromPrice =
    typeof fromPriceUnitAmount === 'number'
      ? convertUnitPriceToFormattedPrice(fromPriceUnitAmount, currency)
      : null;

  // Three flexible chips and a fixed-width "+", as the compact card is drawn;
  // the featured card runs three across on mobile and four on desktop.
  const slotGrid = (
    <div
      className={classNames(
        'grid gap-ds-sm',
        isFeatured
          ? 'grid-cols-3 lg:grid-cols-4'
          : classNames(
              'lg:px-ds-xs lg:pb-ds-xs',
              hiddenCount > 0
                ? 'grid-cols-[repeat(3,minmax(0,1fr))_2rem] lg:grid-cols-[repeat(3,minmax(0,1fr))_2.5rem]'
                : 'grid-cols-3',
            ),
      )}
    >
      {visible.map((slot) => (
        <SlotChip
          key={slot.id}
          slot={slot}
          isSelected={false}
          isSelectable
          onSelect={(slotId) => onSlotSelect(venueId, slotId)}
          appearance="card"
        />
      ))}
      {!isFeatured && hiddenCount > 0 && (
        <Button
          variant="secondary"
          size="icon"
          isInline
          onClick={() => setIsExpanded(true)}
          aria-label={`Show ${hiddenCount} more times`}
          className="size-8 rounded-lg border-color-border-input-lightmode dark:border-color-border-input-darkmode lg:size-10"
          iconLeft={<PlusPlain className="h-5 w-5" />}
        />
      )}
    </div>
  );

  if (!isFeatured) {
    return (
      <div className={classNames('flex flex-col gap-ds-md lg:gap-ds-lg', className)}>
        <CardVenue
          id={venueId}
          slug={slug}
          imageUrl={imageUrl}
          title={title}
          addressString={addressString}
          accessType={accessType}
          totalCourtCount={totalCourtCount}
          pickleballNets={pickleballNets}
          primarySport={primarySport}
        />
        {slotGrid}
      </div>
    );
  }

  // Desktop puts the photo beside the details; mobile stacks them.
  return (
    <div
      className={classNames(
        'rounded-3xl border border-color-border-card-lightmode p-ds-sm shadow-feed-card dark:border-color-border-card-darkmode',
        'lg:rounded-[2.5rem] lg:p-ds-xl',
        'flex flex-col gap-ds-md lg:flex-row lg:items-start lg:gap-ds-xl',
        className,
      )}
    >
      {imageUrl && (
        <Link href={getCourtPageUrl(slug)} className="shrink-0 lg:w-[49.3%]">
          <img
            src={imageUrl}
            alt=""
            aria-hidden
            className="aspect-[325/185] w-full rounded-2xl object-cover lg:aspect-auto lg:h-[21.25rem] lg:rounded-[1.25rem]"
          />
        </Link>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-ds-md lg:h-[21.25rem] lg:justify-between lg:gap-ds-xl">
        <Link href={getCourtPageUrl(slug)} className="flex flex-col gap-ds-xs lg:gap-ds-sm">
          <span className="flex items-center justify-between gap-ds-sm">
            {/* Figma names the club's sport only on the wider card. */}
            {primarySport && (
              <SportBadge sport={primarySport} size="xs" className="max-lg:hidden" />
            )}
            {fromPrice && (
              <span className="ml-auto flex items-baseline gap-ds-xs lg:gap-ds-sm">
                <span className="typography-product-text-card-mobile lg:typography-product-caption text-color-text-lightmode-placeholder dark:text-color-text-darkmode-placeholder">
                  from
                </span>
                <span className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {fromPrice.priceDisplay}
                </span>
              </span>
            )}
          </span>

          <p className="typography-product-display-mobile lg:typography-product-display-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {title}
          </p>

          <span className="typography-product-text-card-mobile flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            <Location className="h-4 w-4 shrink-0" />
            {addressString}
          </span>
        </Link>

        {/* The times sit at the foot of the card, level with the bottom of the photo. */}
        <div className="flex flex-col gap-ds-md lg:gap-ds-xl">
          {slotGrid}

          {hiddenCount > 0 && (
            <Button
              variant="secondary"
              size="md"
              iconLeft={<PlusPlain className="h-4 w-4" />}
              label="More hours"
              onClick={() => setIsExpanded(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
